import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Input, Textarea } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

type EmailProvider = 'microsoft' | 'google' | 'smtp';
type EmailAccountStatus = 'connected' | 'disconnected' | 'error' | 'pending';

interface EmailAccount {
  id: string;
  provider: EmailProvider;
  display_name: string;
  sender_address: string;
  status: EmailAccountStatus;
  last_send_success_at: string | null;
  last_send_error: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_TONE: Record<EmailAccountStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  connected: 'success',
  disconnected: 'neutral',
  pending: 'warning',
  error: 'danger',
};

const PROVIDER_LABEL: Record<EmailProvider, string> = {
  microsoft: 'Microsoft 365',
  google: 'Google Workspace',
  smtp: 'SMTP',
};

export function EmailAccountDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [testTo, setTestTo] = useState('');
  const [testSubject, setTestSubject] = useState('XSC test message');
  const [testBody, setTestBody] = useState(
    'This is a test email sent from x-streamConnect to verify the email account configuration.',
  );
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const accountQ = useQuery({
    queryKey: ['email-account', id],
    queryFn: () => api.get(`/v1/email-accounts/${id}`) as Promise<EmailAccount>,
    enabled: !!id,
  });

  const deleteOne = useMutation({
    mutationFn: () => api.delete(`/v1/email-accounts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      navigate('/email-accounts');
    },
  });

  const sendTest = useMutation({
    mutationFn: (payload: unknown) =>
      api.post(`/v1/email-accounts/${id}/send-test`, payload) as Promise<{
        message_id: string | null;
      }>,
    onSuccess: (resp) => {
      setTestError(null);
      setTestResult(resp.message_id
        ? `Sent. Provider message id: ${resp.message_id}`
        : 'Sent (provider returned no message id).');
    },
    onError: (err) => {
      setTestResult(null);
      setTestError(extractDetail(err));
    },
  });

  const onTestSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setTestResult(null);
    setTestError(null);
    sendTest.mutate({
      to: [{ email: testTo.trim() }],
      subject: testSubject.trim(),
      text: testBody,
    });
  };

  if (accountQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (accountQ.error) return <FormBanner message={extractDetail(accountQ.error)} />;
  if (!accountQ.data) return <FormBanner message="Email account not found" />;
  const a = accountQ.data;

  return (
    <div className="max-w-3xl space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/email-accounts" className="text-sm text-ink-muted hover:text-ink">← Email accounts</Link>
          <h1 className="text-2xl font-semibold mt-1">{a.display_name}</h1>
          <div className="flex items-center gap-2 mt-1 text-sm">
            <Badge tone={STATUS_TONE[a.status]}>{a.status}</Badge>
            <span className="text-ink-muted">{PROVIDER_LABEL[a.provider]}</span>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            if (window.confirm(`Delete email account "${a.display_name}"?`)) deleteOne.mutate();
          }}
          disabled={deleteOne.isPending}
        >
          Delete
        </Button>
      </header>

      <Card>
        <CardHeader><CardTitle>Account</CardTitle></CardHeader>
        <CardBody>
          <dl className="grid grid-cols-[160px_1fr] gap-y-2 text-sm">
            <dt className="text-ink-muted">Sender address</dt>
            <dd>
              <a className="text-accent hover:underline" href={`mailto:${a.sender_address}`}>
                <code className="text-xs">{a.sender_address}</code>
              </a>
            </dd>
            <dt className="text-ink-muted">Provider</dt>
            <dd>{PROVIDER_LABEL[a.provider]}</dd>
            <dt className="text-ink-muted">Last successful send</dt>
            <dd>{a.last_send_success_at
              ? new Date(a.last_send_success_at).toLocaleString()
              : <span className="text-ink-muted">never</span>}
            </dd>
            <dt className="text-ink-muted">Last error</dt>
            <dd>{a.last_send_error
              ? <span className="text-danger">{a.last_send_error}</span>
              : <span className="text-ink-muted">none</span>}
            </dd>
            <dt className="text-ink-muted">Created</dt>
            <dd>{new Date(a.created_at).toLocaleString()}</dd>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader><CardTitle>Send a test message</CardTitle></CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-ink-muted">
            Sends a real email through this account so you can verify connectivity. Provider
            response surfaces in the result below.
          </p>
          <form onSubmit={onTestSubmit} className="space-y-3">
            <FormField label="To" required>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              )}
            </FormField>
            <FormField label="Subject" required>
              {(id) => (
                <Input
                  id={id}
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                  required
                />
              )}
            </FormField>
            <FormField label="Body">
              {(id) => (
                <Textarea
                  id={id}
                  rows={4}
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                />
              )}
            </FormField>
            <div className="flex justify-end">
              <Button type="submit" disabled={sendTest.isPending || !testTo.trim()}>
                {sendTest.isPending ? 'Sending…' : 'Send test'}
              </Button>
            </div>
          </form>
          {testResult && <p className="text-sm text-success">{testResult}</p>}
          {testError && <p className="text-sm text-danger">{testError}</p>}
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
